import { supabase } from '../supabaseClient';
import { sendAccountApprovalEmail } from './email';

// =====================================================
// ADMIN API FUNCTIONS
// =====================================================

export interface AdminStats {
  totalUsers: number;
  totalProjects: number;
  totalOffers: number;
  totalReviews: number;
  activeFreelancers: number;
  activeClients: number;
  pendingVerifications: number;
  totalEarnings: number;
  recentRegistrations: number;
  recentProjects: number;
}

export interface UserProfile {
  id: string;
  role: 'CLIENT' | 'FREELANCER' | 'AGENCY' | 'ADMIN';
  status: 'ACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION';
  full_name: string;
  email: string;
  phone: string | null;
  company: string | null;
  company_bulstat: string | null;
  bio: string | null;
  skills: string[];
  portfolio_url: string | null;
  hourly_rate: number | null;
  rating_avg: number;
  rating_cnt: number;
  completed_jobs: number;
  total_earnings: number;
  verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectData {
  id: string;
  client_id: string;
  title: string;
  description: string;
  budget_min: number | null;
  budget_max: number | null;
  deadline: string | null;
  category_id: string | null;
  skills: string[];
  status: 'DRAFT' | 'PENDING' | 'PUBLISHED' | 'CLOSED' | 'CANCELLED';
  featured: boolean;
  views_count: number;
  offers_count: number;
  created_at: string;
  updated_at: string;
  client_name: string;
  client_company: string | null;
  category_name: string | null;
}

export interface OfferData {
  id: string;
  project_id: string;
  provider_id: string;
  message: string;
  price: number;
  delivery_days: number;
  status: 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'WITHDRAWN';
  created_at: string;
  updated_at: string;
  project_title: string;
  provider_name: string;
  provider_company: string | null;
}

export interface CreditTransaction {
  id: string;
  user_id: string;
  package_id: string | null;
  type: 'PURCHASE' | 'USAGE' | 'REFUND' | 'BONUS';
  amount: number;
  description: string | null;
  stripe_payment_intent_id: string | null;
  created_at: string;
  user_name: string;
  package_name: string | null;
}

// =====================================================
// STATISTICS FUNCTIONS
// =====================================================

export async function getAdminStats(): Promise<{
  data: AdminStats | null;
  error: string | null;
}> {
  try {
    const { data, error } = await supabase.rpc('get_platform_stats');
    
    if (error) {
      console.error('Error getting admin stats:', error);
      return { data: null, error: error.message };
    }

    // Get additional stats
    const [pendingUsers, recentRegistrations, recentProjects] = await Promise.all([
      supabase
        .from('profiles')
        .select('id', { count: 'exact' })
        .eq('status', 'PENDING_VERIFICATION'),
      supabase
        .from('profiles')
        .select('id', { count: 'exact' })
        .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()),
      supabase
        .from('projects')
        .select('id', { count: 'exact' })
        .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
    ]);

    const stats: AdminStats = {
      totalUsers: data?.[0]?.total_users || 0,
      totalProjects: data?.[0]?.total_projects || 0,
      totalOffers: data?.[0]?.total_offers || 0,
      totalReviews: data?.[0]?.total_reviews || 0,
      activeFreelancers: data?.[0]?.active_freelancers || 0,
      activeClients: data?.[0]?.active_clients || 0,
      pendingVerifications: pendingUsers.count || 0,
      totalEarnings: parseFloat(data?.[0]?.total_earnings || '0'),
      recentRegistrations: recentRegistrations.count || 0,
      recentProjects: recentProjects.count || 0,
    };

    return { data: stats, error: null };
  } catch (error) {
    console.error('Error in getAdminStats:', error);
    return { data: null, error: 'Неочаквана грешка при зареждане на статистики' };
  }
}

// =====================================================
// USER MANAGEMENT FUNCTIONS
// =====================================================

export async function getUsers(params: {
  page?: number;
  limit?: number;
  role?: string;
  status?: string;
  search?: string;
}): Promise<{
  data: UserProfile[];
  error: string | null;
  count: number;
}> {
  try {
    const { page = 0, limit = 20, role, status, search } = params;
    
    let query = supabase
      .from('profiles')
      .select('*', { count: 'exact' })
      .range(page * limit, (page + 1) * limit - 1)
      .order('created_at', { ascending: false });

    if (role) {
      query = query.eq('role', role);
    }

    if (status) {
      query = query.eq('status', status);
    }

    if (search) {
      query = query.or(`full_name.ilike.%${search}%,email.ilike.%${search}%,company.ilike.%${search}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error getting users:', error);
      return { data: [], error: error.message, count: 0 };
    }

    return { data: data || [], error: null, count: count || 0 };
  } catch (error) {
    console.error('Error in getUsers:', error);
    return { data: [], error: 'Неочаквана грешка при зареждане на потребители', count: 0 };
  }
}

export async function updateUserStatus(
  userId: string, 
  status: 'ACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION'
): Promise<{ success: boolean; error: string | null }> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ status })
      .eq('id', userId);

    if (error) {
      console.error('Error updating user status:', error);
      return { success: false, error: error.message };
    }

    // Send email notification if account is approved
    if (status === 'ACTIVE') {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, email, role')
          .eq('id', userId)
          .single();

        if (profile) {
          await sendAccountApprovalEmail(
            profile.email,
            profile.full_name,
            profile.role
          );
        }
      } catch (emailError) {
        console.error('Email sending error:', emailError);
        // Don't fail status update if email sending fails
      }
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in updateUserStatus:', error);
    return { success: false, error: 'Неочаквана грешка при обновяване на статуса' };
  }
}

export async function updateUserRole(
  userId: string, 
  role: 'CLIENT' | 'FREELANCER' | 'AGENCY' | 'ADMIN'
): Promise<{ success: boolean; error: string | null }> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ role })
      .eq('id', userId);

    if (error) {
      console.error('Error updating user role:', error);
      return { success: false, error: error.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in updateUserRole:', error);
    return { success: false, error: 'Неочаквана грешка при обновяване на ролята' };
  }
}

export async function deleteUser(userId: string): Promise<{ success: boolean; error: string | null }> {
  try {
    const { error } = await supabase
      .from('profiles')
      .delete()
      .eq('id', userId);

    if (error) {
      console.error('Error deleting user:', error);
      return { success: false, error: error.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in deleteUser:', error);
    return { success: false, error: 'Неочаквана грешка при изтриване на потребителя' };
  }
}

// =====================================================
// PROJECT MANAGEMENT FUNCTIONS
// =====================================================

export async function getProjects(params: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}): Promise<{
  data: ProjectData[];
  error: string | null;
  count: number;
}> {
  try {
    const { page = 0, limit = 20, status, search } = params;
    
    let query = supabase
      .from('projects')
      .select(`
        *,
        profiles!projects_client_id_fkey(full_name, company),
        categories(name)
      `, { count: 'exact' })
      .range(page * limit, (page + 1) * limit - 1)
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    if (search) {
      query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error getting projects:', error);
      return { data: [], error: error.message, count: 0 };
    }

    const formattedData: ProjectData[] = (data || []).map(project => ({
      ...project,
      client_name: project.profiles?.full_name || 'Unknown',
      client_company: project.profiles?.company || null,
      category_name: project.categories?.name || null,
    }));

    return { data: formattedData, error: null, count: count || 0 };
  } catch (error) {
    console.error('Error in getProjects:', error);
    return { data: [], error: 'Неочаквана грешка при зареждане на проекти', count: 0 };
  }
}

export async function updateProjectStatus(
  projectId: string, 
  status: 'DRAFT' | 'PENDING' | 'PUBLISHED' | 'CLOSED' | 'CANCELLED'
): Promise<{ success: boolean; error: string | null }> {
  try {
    const { error } = await supabase
      .from('projects')
      .update({ status })
      .eq('id', projectId);

    if (error) {
      console.error('Error updating project status:', error);
      return { success: false, error: error.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in updateProjectStatus:', error);
    return { success: false, error: 'Неочаквана грешка при обновяване на статуса' };
  }
}

export async function deleteProject(projectId: string): Promise<{ success: boolean; error: string | null }> {
  try {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    if (error) {
      console.error('Error deleting project:', error);
      return { success: false, error: error.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in deleteProject:', error);
    return { success: false, error: 'Неочаквана грешка при изтриване на проекта' };
  }
}

// =====================================================
// OFFERS MANAGEMENT FUNCTIONS
// =====================================================

export async function getOffers(params: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}): Promise<{
  data: OfferData[];
  error: string | null;
  count: number;
}> {
  try {
    const { page = 0, limit = 20, status, search } = params;
    
    let query = supabase
      .from('offers')
      .select(`
        *,
        projects!offers_project_id_fkey(title),
        profiles!offers_provider_id_fkey(full_name, company)
      `, { count: 'exact' })
      .range(page * limit, (page + 1) * limit - 1)
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    if (search) {
      query = query.or(`message.ilike.%${search}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error getting offers:', error);
      return { data: [], error: error.message, count: 0 };
    }

    const formattedData: OfferData[] = (data || []).map(offer => ({
      ...offer,
      project_title: offer.projects?.title || 'Unknown Project',
      provider_name: offer.profiles?.full_name || 'Unknown',
      provider_company: offer.profiles?.company || null,
    }));

    return { data: formattedData, error: null, count: count || 0 };
  } catch (error) {
    console.error('Error in getOffers:', error);
    return { data: [], error: 'Неочаквана грешка при зареждане на оферти', count: 0 };
  }
}

// =====================================================
// CREDIT TRANSACTIONS FUNCTIONS
// =====================================================

export async function getCreditTransactions(params: {
  page?: number;
  limit?: number;
  type?: string;
  search?: string;
}): Promise<{
  data: CreditTransaction[];
  error: string | null;
  count: number;
}> {
  try {
    const { page = 0, limit = 20, type, search } = params;
    
    let query = supabase
      .from('credit_transactions')
      .select(`
        *,
        profiles!credit_transactions_user_id_fkey(full_name),
        credit_packages!credit_transactions_package_id_fkey(name)
      `, { count: 'exact' })
      .range(page * limit, (page + 1) * limit - 1)
      .order('created_at', { ascending: false });

    if (type) {
      query = query.eq('type', type);
    }

    if (search) {
      query = query.or(`description.ilike.%${search}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error getting credit transactions:', error);
      return { data: [], error: error.message, count: 0 };
    }

    const formattedData: CreditTransaction[] = (data || []).map(transaction => ({
      ...transaction,
      user_name: transaction.profiles?.full_name || 'Unknown',
      package_name: transaction.credit_packages?.name || null,
    }));

    return { data: formattedData, error: null, count: count || 0 };
  } catch (error) {
    console.error('Error in getCreditTransactions:', error);
    return { data: [], error: 'Неочаквана грешка при зареждане на транзакции', count: 0 };
  }
}

// =====================================================
// CATEGORIES MANAGEMENT FUNCTIONS
// =====================================================

export async function getCategories(): Promise<{
  data: any[];
  error: string | null;
}> {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('Error getting categories:', error);
      return { data: [], error: error.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error in getCategories:', error);
    return { data: [], error: 'Неочаквана грешка при зареждане на категории' };
  }
}

export async function createCategory(categoryData: {
  name: string;
  description?: string;
  parent_id?: string;
  sort_order?: number;
}): Promise<{ success: boolean; error: string | null }> {
  try {
    const { error } = await supabase
      .from('categories')
      .insert(categoryData);

    if (error) {
      console.error('Error creating category:', error);
      return { success: false, error: error.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in createCategory:', error);
    return { success: false, error: 'Неочаквана грешка при създаване на категория' };
  }
}

export async function updateCategory(
  categoryId: string, 
  categoryData: {
    name?: string;
    description?: string;
    parent_id?: string;
    sort_order?: number;
    active?: boolean;
  }
): Promise<{ success: boolean; error: string | null }> {
  try {
    const { error } = await supabase
      .from('categories')
      .update(categoryData)
      .eq('id', categoryId);

    if (error) {
      console.error('Error updating category:', error);
      return { success: false, error: error.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in updateCategory:', error);
    return { success: false, error: 'Неочаквана грешка при обновяване на категория' };
  }
}

export async function deleteCategory(categoryId: string): Promise<{ success: boolean; error: string | null }> {
  try {
    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', categoryId);

    if (error) {
      console.error('Error deleting category:', error);
      return { success: false, error: error.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Error in deleteCategory:', error);
    return { success: false, error: 'Неочаквана грешка при изтриване на категория' };
  }
}
